<template>
  <div
    :id="id ? `daterange_container_${id}` : ''"
    ref="dateRangeDisplay"
    v-click-outside="handleClickOutside"
    class="rs-form-date-range-picker"
    @mouseleave="handleComponentMouseLeave"
  >
    <label
      v-if="label"
      class="d-block"
    >{{ label }}</label>
    <RsButton
      :id="id ? `btn_daterange_${id}` : ''"
      class="date-range-button d-flex align-items-stretch p-0 w-100 text-transform-none"
      @click="handleShowOptions"
    >
      <div class="calendar-icon-container rs-bg-info d-flex h-100 align-items-center justify-content-center flex-shrink-0 rs-text-white">
        <i class="far fa-calendar" />
      </div>
      <div
        class="label-container d-flex h-100 align-items-center flex-fill p-2 text-truncate"
        :title="selectedValue ? selectedValue.label : ''"
      >
        <div class="text-truncate">
          {{ selectedValue ? selectedValue.label : '' }}
        </div>
      </div>
      <div class="chevron-icon-container d-flex h-100 align-items-center p-2 flex-shrink-0">
        <i :class="areOptionsVisible ? 'far fa-chevron-up' : 'far fa-chevron-down'" />
      </div>
    </RsButton>

    <div
      v-show="areOptionsVisible"
      ref="dropdownContainer"
      class="date-range-options-container border rs-bg-white"
      :style="positionStyles"
    >
      <div class="d-flex align-items-stretch">
        <div class="options-select-container py-3 pr-2">
          <div
            v-for="(option, index) in optionsWithCustomRange"
            :key="index"
            class="pb-1 d-flex align-items-center cursor-pointer"
            @click="handleOptionSelect(option)"
          >
            <div class="options-checkmark-icon-container text-center">
              <i
                v-show="isOptionSelected(option)"
                class="far fa-check"
              />
            </div>
            <div class="options-label-container flex-grow-1">
              {{ option.label }}
            </div>
          </div>
        </div>

        <div
          v-show="isCustomPickerVisible"
          class="custom-picker-container border-left flex-grow-1 p-2"
        >
          <div class="text-center">
            <label class="mb-0 font-weight-normal">
              {{ (selectedValue || {}).label }}
            </label>
          </div>
          <div class="controls d-flex align-items-stretch mb-2">
            <RsButton
              variant="border"
              @click="handleMonthChange(-1)"
            >
              <i class="far fa-chevron-left" />
            </RsButton>
            <RsFormSelect
              v-model="datepickerMonthYear.month"
              class="flex-grow-1 ml-1 mb-0"
              :options="months"
            />
            <RsFormSelect
              v-model="datepickerMonthYear.year"
              class="flex-grow-1 mx-1 mb-0"
              :options="years"
            />
            <RsButton
              variant="border"
              @click="handleMonthChange(1)"
            >
              <i class="far fa-chevron-right" />
            </RsButton>
          </div>

          <div class="weekdays d-flex align-items-center text-center">
            <div
              v-for="day in weekdays"
              :key="day.value"
              class="flex-grow-1 weekday"
            >
              <label class="mb-0">{{ day.text }}</label>
            </div>
          </div>
          <div class="days row m-0 align-items-center text-center">
            <div
              v-for="day in pickerDays"
              :key="day.dayOfMonth"
              class="day py-1 flex-grow-0 cursor-pointer"
              @click="handleCustomDateSelect(day.value)"
              @mouseover="handleDayMouseover(day.value)"
            >
              <div
                v-show="day.dayOfMonth > 0"
                :class="isCustomDateInRange(day.value)"
              >
                {{ day.dayOfMonth }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import dayjs, { Dayjs } from 'dayjs';
import { defineComponent, PropType } from 'vue';
import { ISelectOption } from '@/shared';
import { months, weekdays } from '@/shared/helpers/static-select-options';

/**
 * The type emitted by the component on `input` event
 */
export type TDateRange = {
  minDate?: Date;
  maxDate?: Date;
};

/**
 * Range option typing
 */
export type TRangeOption = TDateRange & {
  label: string;
  isCustom?: boolean;
  exactMin?: boolean;
  exactMax?: boolean;
};

const dateFormat = 'MMMM D, YYYY';
const initialDropdownStyles = { position: 'absolute', left: 0, width: 'auto' };
const dropdownExpandedWidth = 475;

/**
 * When dates are selected, they output as JS Date objects!
 */
export default defineComponent({
  props: {
    /**
     * Input value that implements the `v-model` cycle (the `$emit('input') / `value` prop cycle.)
     */
    value: {
      type: Object as PropType<TDateRange>,
      default: () => ({
        minDate: dayjs().subtract(7, 'days').startOf('day').toDate(),
        maxDate: dayjs().endOf('day').toDate(),
      }),
    },

    /**
     * Vue 3 v-model binding. Kept alongside the legacy `value`/`input` pair so
     * both `v-model` (modelValue/update:modelValue) and explicit `:value`+`@input`
     * consumers work. `undefined` means the parent did not bind v-model, so we
     * fall back to `value` (see the `boundValue` computed).
     */
    modelValue: {
      type: Object as PropType<TDateRange>,
      default: undefined,
    },

    /**
     * Common label for the control
     */
    label: {
      type: String,
      default: null,
    },

    /**
     * Control ID attribute
     */
    id: {
      type: String,
      default: null,
    },

    /**
     * The date range options. Draws from default values but can be overridden by prop values.
     */
    options: {
      type: Array as PropType<TRangeOption[]>,
      default: () => [],
    },

    noFuture: {
      type: Boolean,
      default: false,
    },

    yearLimit: {
      type: Number,
      default: null,
    },

    excludeTodayForPresets: {
      type: Boolean,
      default: false,
    },

    hideCustomRangeControl: {
      type: Boolean,
      default: false,
    },

    hideEmptyOption: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['input', 'update:modelValue'],

  data() {
    return {
      weekdays,
      months,
      selectedValue: null as TRangeOption | null,
      areOptionsVisible: false as boolean,
      isCustomPickerVisible: false as boolean,
      calendarHoverValue: null as Dayjs | null,
      datepickerMonthYear: {
        year: dayjs().year(),
        month: dayjs().month(),
      },
      isMounted: false as boolean,
      isMinSelect: true as boolean,
    };
  },

  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): TDateRange {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },

    /**
     * Used to determine where the control is on the screen, which determines
     * if the dropdown should open clamped to the left or right of the control.
     * AKA, `left: 0` or `right: 0` depending on `window` cutoff.
     */
    positionStyles(): Record<string, string | number> {
      if (this.isMounted && this.areOptionsVisible) {
        const dropdown = this.$refs.dropdownContainer as Element;
        const display = this.$refs.dateRangeDisplay as Element;
        const displayDims = display.getBoundingClientRect();
        const dropdownDims = dropdown.getBoundingClientRect();
        const distanceFromRight = window.innerWidth - displayDims.left - displayDims.width;
        const distanceFromTop = displayDims.y + displayDims.height + 16;
        const clampRight = (
          dropdownDims.right + dropdownExpandedWidth > window.innerWidth
          || displayDims.right + dropdownExpandedWidth > window.innerWidth
        );

        return {
          position: 'fixed',
          right: clampRight && this.isCustomPickerVisible ? `${distanceFromRight}px` : 'unset',
          left: clampRight && this.isCustomPickerVisible ? 'unset' : `${displayDims.left}px`,
          top: distanceFromTop,
          width: this.isCustomPickerVisible ? `${dropdownExpandedWidth}px` : 'auto',
          zIndex: 1000,
        };
      }

      return initialDropdownStyles;
    },

    /**
     * Give me 50 years before and after the current year to populate my year select options in the custom calendar picker.
     */
    years(): ISelectOption[] {
      let low = dayjs().year() - this.yearLimit || 50;
      const high = this.noFuture ? dayjs().year() : dayjs().year() + this.yearLimit || 50;
      const options: ISelectOption[] = [];

      while (low <= high) {
        options.push({ value: low, text: `${low}` });
        low += 1;
      }

      return options.sort((a, b) => (a > b ? 1 : -1));
    },

    /**
     * Append the custom range option to the default or `props.options` range options.
     */
    optionsWithCustomRange(): (TRangeOption & { isCustom?: boolean })[] {
      return [
        ...(this.hideEmptyOption ? [] : [{
          label: this.$ts('dateRangePicker.selectDate'),
          isCustom: false,
          minDate: undefined,
          maxDate: undefined,
        }]),
        ...this.defaultOptions,
        ...(this.hideCustomRangeControl ? [] : [{
          label: this.$ts('dateRangePicker.customRange'),
          isCustom: true,
          minDate: undefined,
          maxDate: undefined,
        }]),
      ];
    },

    /**
     * Get the days of the month / year combo selected in the custom calendar picker.
     * Offsets by the days leftover in the week. For example, if the month started on a Wednesday, account for Sunday to Tuesday
     * in the previous month so that the days display on the correct weekday on the calendar control.
     */
    pickerDays(): { dayOfWeek: number, dayOfMonth: number, value: Date }[] {
      const monthDays = dayjs(`${this.datepickerMonthYear.year}-${this.datepickerMonthYear.month + 1}-01`).daysInMonth();
      const options: { dayOfWeek: number, dayOfMonth: number, value: Date }[] = [];

      while (options.length !== monthDays) {
        const day = dayjs(`${this.datepickerMonthYear.year}-${this.datepickerMonthYear.month + 1}-${options.length + 1}`);
        options.push({ dayOfWeek: day.day(), dayOfMonth: options.length + 1, value: day.toDate() });
      }

      let leftovers = options[0].dayOfWeek - 1;

      while (leftovers >= 0) {
        options.unshift({ dayOfWeek: leftovers, dayOfMonth: 0 - leftovers, value: dayjs().toDate() });
        leftovers -= 1;
      }

      return options;
    },

    defaultOptions(): TRangeOption[] {
      return this.options?.length ? this.options : [
        {
          label: this.$ts('dateRangePicker.lastDays', { count: 7 }),
          minDate: dayjs().subtract(6, 'days').startOf('day').toDate(),
          maxDate: dayjs().endOf('day').toDate(),
        },
        {
          label: this.$ts('dateRangePicker.lastDays', { count: 14 }),
          minDate: dayjs().subtract(13, 'days').startOf('day').toDate(),
          maxDate: dayjs().endOf('day').toDate(),
        },
        {
          label: this.$ts('dateRangePicker.lastDays', { count: 30 }),
          minDate: dayjs().subtract(29, 'days').startOf('day').toDate(),
          maxDate: dayjs().endOf('day').toDate(),
        },
        {
          label: this.$ts('dateRangePicker.lastDays', { count: 60 }),
          minDate: dayjs().subtract(59, 'days').startOf('day').toDate(),
          maxDate: dayjs().endOf('day').toDate(),
        },
        {
          label: this.$ts('dateRangePicker.lastDays', { count: 90 }),
          minDate: dayjs().subtract(89, 'days').startOf('day').toDate(),
          maxDate: dayjs().endOf('day').toDate(),
        },
        {
          label: this.$ts('dateRangePicker.lastMonths', { count: 6 }),
          minDate: dayjs().subtract(6, 'months').startOf('day').toDate(),
          maxDate: dayjs().endOf('day').toDate(),
        },
        {
          label: this.$ts('dateRangePicker.lastMonths', { count: 12 }),
          minDate: dayjs().subtract(12, 'months').toDate(),
          maxDate: dayjs().endOf('day').toDate(),
        },
        {
          label: this.$ts('dateRangePicker.currentMonth'),
          minDate: !this.excludeTodayForPresets
            ? dayjs().startOf('month').startOf('day').toDate()
            : dayjs()
              .startOf('month')
              .add(1, 'day')
              .startOf('day')
              .toDate(),
          maxDate: dayjs().endOf('day').toDate(),
        },
        {
          label: this.$ts('dateRangePicker.previousMonth'),
          minDate: dayjs()
            .subtract(1, 'months')
            .startOf('month')
            .startOf('day')
            .toDate(),
          maxDate: dayjs()
            .subtract(1, 'months')
            .endOf('month')
            .endOf('day')
            .toDate(),
        },
      ];
    },
  },

  watch: {
    boundValue: {
      immediate: true,
      handler() {
        this.setSelectedFromValue();
      },
    },

    areOptionsVisible() {
      this.isCustomPickerVisible = !!this.selectedValue?.isCustom;
    },

    isCustomPickerVisible() {
      this.isMinSelect = true;
    },
  },

  mounted() {
    this.isMounted = true;
  },

  methods: {
    /**
     * When the date picker dropdown opens, we determine if user input has been given via the `value` prop.
     */
    getSelectedValueFromInput(): TRangeOption {
      const valMin = dayjs(this.boundValue.minDate).startOf('day');
      const valMax = dayjs(this.boundValue.maxDate).endOf('day');
      const maxDateIsYesterday = valMax.isSame(dayjs().subtract(1, 'day'), 'day');

      const valMinActual = this.excludeTodayForPresets && maxDateIsYesterday ? valMin.add(1, 'day').startOf('day') : valMin;
      const valMaxActual = this.excludeTodayForPresets && maxDateIsYesterday ? valMax.add(1, 'day').endOf('day') : valMax;

      const optionMatch = this.defaultOptions.find((opt) => (
        valMinActual.isSame(dayjs(opt.minDate), 'date')
        && valMaxActual.isSame(dayjs(opt.maxDate), 'date')
      ));

      return optionMatch ?? {
        ...this.optionsWithCustomRange.find((opt) => opt.isCustom) ?? { isCustom: true },
        ...this.boundValue,
        label: `${valMin.format(dateFormat)} - ${valMax.format(dateFormat)}`,
      };
    },

    /**
     * A boolean return that checks if an option is selected from the initial options, or if it's a custom range selected.
     * @param option `TRangOption`
     */
    isOptionSelected(option: TRangeOption) {
      if (
        !option.isCustom
        && !option.minDate
        && !this.selectedValue?.minDate
        && !this.selectedValue?.isCustom
      ) {
        return true;
      }

      if (option.isCustom && this.selectedValue?.isCustom) {
        return true;
      }

      return (
        this.selectedValue?.minDate
        && dayjs(this.selectedValue?.minDate).isSame(dayjs(option.minDate), 'date')
        && dayjs(this.selectedValue?.maxDate).isSame(dayjs(option.maxDate), 'date')
      );
    },

    /**
     * Determines if the minimum date is already selected in the custom picker.
     * @param value `Date` just a JS Date
     */
    isCustomDateInRange(value: Date) {
      if (this.selectedValue?.minDate) {
        const min = dayjs(this.selectedValue?.minDate);
        const max = dayjs(this.selectedValue?.maxDate);
        const val = dayjs(value);

        if (val.isSame(min, 'day') || val.isSame(max, 'day')) {
          return 'selected';
        }

        if (this.calendarHoverValue && val.isSame(this.calendarHoverValue, 'day')) {
          return 'selected';
        }

        if (val.isBetween(min, max, 'day')) {
          return 'active';
        }

        if (this.calendarHoverValue && val.isBetween(min, this.calendarHoverValue, 'day')) {
          return 'active';
        }
      }

      return '';
    },

    /**
     * Determines if the minimum date is already selected in the custom picker.
     */
    isMinSelected(value: Date) {
      return !this.isMinSelect && dayjs(value).isBefore(this.selectedValue?.minDate);
    },

    /**
     * Makes some decisions based on the custom date range selected, including whether it's min / max date.
     * This method is used to also determine what "selection" state we have. Like if we are picking a new date
     * range or the second of 2 dates (max date).
     */
    handleCustomDateSelect(value: Date) {
      if (this.selectedValue) {
        const day = dayjs(value);
        const min = dayjs(this.selectedValue?.minDate);

        /**
         * If I'm picking a min date
         */
        if (this.isMinSelect) {
          this.selectedValue.minDate = value;
          this.selectedValue.maxDate = value;
          this.selectedValue.label = `${day.format(dateFormat)}`;
          this.isMinSelect = false;
          return;
        }

        /**
         * If I'm picking a max date that is before the min date, we change the min date.
         */
        if (!this.isMinSelect && day.isBefore(min)) {
          this.selectedValue.minDate = value;
          this.selectedValue.maxDate = value;
          this.selectedValue.label = `${day.format(dateFormat)}`;
          return;
        }

        /**
         * If I'm picking a max date that lands on the same day or after the min date
         */
        if (!this.isMinSelect && day.isSameOrAfter(min, 'date')) {
          this.selectedValue.maxDate = value;
          this.selectedValue.label = day.isSame(min, 'date')
            ? this.selectedValue.label
            : `${this.selectedValue.label} - ${day.format(dateFormat)}`;

          this.isMinSelect = true;
          this.handleModelEmit();
        }
      }
    },

    /**
     * Show the options dropdown when clicking the date picker control
     */
    handleShowOptions() {
      this.areOptionsVisible = !this.areOptionsVisible;

      if (!this.areOptionsVisible) {
        this.setSelectedFromValue();
      }
    },

    /**
     * When option is selected, assign it to the selectedValue state.
     * If not a custom option (selected in calendar), emit the value, otherwise, open the calendar.
     * @param option `TRangeOption`
     */
    handleOptionSelect(option: TRangeOption) {
      if (!option.isCustom) {
        this.selectedValue = option;
        this.handleModelEmit();
      } else {
        this.selectedValue = {
          ...this.selectedValue!,
          minDate: undefined,
          maxDate: undefined,
          label: 'Select a Start and End Date',
          isCustom: true,
        };

        this.isCustomPickerVisible = true;
      }
    },

    /**
     * Emits the min and max date selected, as JS Date objects
     */
    handleModelEmit() {
      this.areOptionsVisible = false;
      const maxDateIsToday = this.selectedValue?.maxDate ? dayjs(this.selectedValue?.maxDate).isSame(dayjs(), 'day') : false;

      if (this.excludeTodayForPresets && !this.selectedValue?.isCustom && maxDateIsToday) {
        const out = {
          minDate: this.selectedValue?.minDate ? dayjs(this.selectedValue?.minDate).subtract(1, 'day').startOf('day').toDate() : undefined,
          maxDate: this.selectedValue?.maxDate ? dayjs(this.selectedValue?.maxDate).subtract(1, 'day').endOf('day').toDate() : undefined,
        };
        this.$emit('input', out);
        this.$emit('update:modelValue', out);
        return;
      }

      const out = {
        minDate: this.selectedValue?.minDate ? dayjs(this.selectedValue?.minDate).startOf('day').toDate() : undefined,
        maxDate: this.selectedValue?.maxDate ? dayjs(this.selectedValue?.maxDate).endOf('day').toDate() : undefined,
      };
      this.$emit('input', out);
      this.$emit('update:modelValue', out);
    },

    /**
     * Handler for when a different month is selected.
     * @param value month number to change to
     */
    handleMonthChange(value: number) {
      this.datepickerMonthYear.month += value;

      if (this.datepickerMonthYear.month > 11) {
        this.datepickerMonthYear = { month: 0, year: this.datepickerMonthYear.year + 1 };
      }

      if (this.datepickerMonthYear.month < 0) {
        this.datepickerMonthYear = { month: 11, year: this.datepickerMonthYear.year - 1 };
      }
    },

    /**
     * Handler for when we click outside of the form control via custom directive.
     * When clicking outside, hide the dropdown menu and reset the selected value.
     */
    handleClickOutside() {
      this.areOptionsVisible = false;
      this.setSelectedFromValue();
    },

    /**
     * Handler for when we mouse over dates in the custom picker.
     * Used to assign a "hover" value that is used to calculate if an "active" class should be applied to
     * the day elements on the custom picker.
     */
    handleDayMouseover(value: Date) {
      const val = dayjs(value);
      if (!this.isMinSelect && val.isAfter(this.selectedValue?.minDate, 'day')) {
        this.calendarHoverValue = val;
        return;
      }

      this.calendarHoverValue = null;
    },

    /**
     * Handler for when we mouse out of the component altogether
     * Used to set `calendarHoverValue` back to a null state.
     */
    handleComponentMouseLeave() {
      this.calendarHoverValue = null;
    },

    /**
     * Sets the selectedValue state from inputted `value` prop.
     */
    setSelectedFromValue() {
      if (this.boundValue?.minDate && this.boundValue?.maxDate) {
        this.selectedValue = this.getSelectedValueFromInput();
      } else {
        [this.selectedValue] = this.optionsWithCustomRange;
      }

      this.datepickerMonthYear = {
        month: this.boundValue?.minDate?.getMonth() ?? dayjs().month(),
        year: this.boundValue?.minDate?.getFullYear() ?? dayjs().year(),
      };
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';

$iconWidth: 42.78px;

.rs-form-date-range-picker {
  position: relative;
  z-index: 1000;

  .date-range-button {
    background: $white;
    text-align: left;
    border: none;
    border-radius: 0;
    color: $label-gray;
    font-weight: normal;

    &:hover {
      background: $white;
      border: none;
    }

    .calendar-icon-container {
      border-top-left-radius: $input-radius;
      border-bottom-left-radius: $input-radius;
      font-size: 1.2rem;
      width: $iconWidth;
    }

    .label-container, .chevron-icon-container {
      border-top: solid 1px $tertiary-gray;
      border-bottom: solid 1px $tertiary-gray;
    }

    .chevron-icon-container {
      border-right: solid 1px $tertiary-gray;
      border-top-right-radius: $input-radius;
      border-bottom-right-radius: $input-radius;
    }
  }

  .date-range-options-container {
    position: fixed;
    left: 0;
    margin-top: 0.25rem;
    border-radius: $input-radius;
    overflow: hidden;
    z-index: 1000;

    .options-select-container {
      width: 185px;
      background: $greige-100;
    }

    .options-checkmark-icon-container {
      width: $iconWidth;
    }
  }

  .custom-picker-container {
    width: 300px;
    .day, .weekday {
      text-align: center;
      flex: 1 1 14.285%;
    }

    .active {
      background: lighten($primary-color, 55%);
    }

    .selected {
      background: $primary-color;
      color: $white;
      padding-top: 3px;
      padding-bottom: 3px;
      border-radius: 5px;
    }
  }
}
</style>
